'use client';
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect, SyntheticEvent,
} from 'react';

import styles from './styles.module.scss';
import Container from '@/components/primitives/container';
import Typography from '@/components/primitives/typography';
import { XIcon } from 'lucide-react';

interface OpenModal {
  title?: string;
  content: ReactNode;
}

interface ModalProps {
  isOpen: boolean;
  title?: string;
  onClose: () => void;
  children: ReactNode;
}

// Define the context interface
interface ModalContextProps {
  isOpen: boolean;
  content?: ReactNode;
  openModal: (modal: OpenModal) => void;
  closeModal: () => void;
}

// Create the context with default values
const ModalContext = createContext<ModalContextProps>({
  isOpen: false,
  content: null,
  openModal: () => {
  },
  closeModal: () => {
  },
});

interface ModalProviderProps {
  children: ReactNode;
}

const Modal = (props: ModalProps) => {
  const [isTop, setTop] = useState(true);
  const modal = useModal();

  const onScroll = useCallback((e: SyntheticEvent<HTMLDivElement>) => {
    setTop(e.currentTarget.scrollTop <= 0);
  }, [setTop]);

  // 닫힌 모달도 전환 애니메이션을 위해 화면 밖에 렌더해 두므로, inert로 초점·보조기기에서 뺀다.
  return <div className={`${styles.background} ${props.isOpen ? styles.show : ''}`} inert={!props.isOpen}>
    <Container className={`${styles.container} ${props.isOpen ? styles.show : ''}`} onScroll={onScroll}>
      <div className={styles.header}>
        <div className={styles.leftElement}></div>
        <Typography className={`${styles.title} ${isTop ? '' : styles.titleScrolled}`} size="large"
                    weight="bold">{props.title}</Typography>
        <button type="button" className={`${styles.close} focus-ring`} aria-label="닫기" onClick={modal.closeModal}>
          <XIcon size={20} />
        </button>
      </div>
      <div className={styles.children}>
        {props.children}
      </div>
    </Container>
  </div>;
};

export const ModalProvider = ({ children }: ModalProviderProps) => {
  // State for modal visibility and content
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState<ReactNode | null>(null);
  const [title, setTitle] = useState<string>();

  // Function to open the modal with specific content
  const openModal = useCallback((modal: OpenModal) => {
    setContent(modal.content);
    setTitle(modal.title);
    setIsOpen(true);
  }, []);

  // Function to close the modal
  const closeModal = useCallback(() => {
    setIsOpen(false);
    // Clear content after animation completes
    setTimeout(() => {
      setContent(null);
    }, 300);
  }, []);

  // Effect to prevent scrolling when modal is open
  useEffect(() => {
    if (typeof document !== 'undefined') {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }

    // Cleanup function to ensure we reset the overflow style when component unmounts
    return () => {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen]);

  return (
    <ModalContext.Provider
      value={{
        isOpen,
        content,
        openModal,
        closeModal,
      }}
    >

      {children}

      <Modal isOpen={isOpen} onClose={closeModal} title={title}>
        {content}
      </Modal>
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  return useContext(ModalContext);
};
